import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Modal,
  Select,
  Table,
  TableBody,
  TextareaAutosize,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import React from 'react';
import {
  RetroPayAffectedPayPeriod,
  RetroPayMatchedWeek,
  RetroPayTargetProject,
  RetroPayWeek,
} from 'types';

import { retroStyles } from './styles';
import { UseRetroPay } from './useRetroPay';

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#f8f8fa',
    color: '#979598',
    fontFamily: 'Red Hat Display SemiBold',
    fontSize: 12,
    lineHeight: '15px',
    padding: 8,
    paddingBottom: 12,
    paddingLeft: 0,
    paddingTop: 12,
  },
  [`&.${tableCellClasses.body}`]: {
    backgroundColor: '#fff',
    borderColor: '#d4d4d6',
    borderWidth: 1,
    color: '#241A2E',
    fontFamily: 'Red Hat Display Medium',
    fontSize: 13,
    lineHeight: '15px',
    padding: 5,
    paddingBottom: 15,
    paddingTop: 25,
    verticalAlign: 'top',
  },
}));

const StyledTableRow = styled(TableRow)(() => ({}));

type RetroMessageKind = 'error' | 'info' | 'success' | 'warning';
const retroMessagePalette: Record<
  RetroMessageKind,
  { bgcolor: string; color: string }
> = {
  error: { bgcolor: '#FDECEA', color: '#BF0000' },
  info: { bgcolor: '#EEF2F8', color: '#3A5A8C' },
  success: { bgcolor: '#EDF7ED', color: '#1D9900' },
  warning: { bgcolor: '#FFF6E9', color: '#E75000' },
};
const RetroMessage = ({
  kind,
  children,
}: {
  kind: RetroMessageKind;
  children: React.ReactNode;
}) => (
  <Typography
    component={'div'}
    sx={{
      ...retroMessagePalette[kind],
      borderRadius: '6px',
      fontFamily: 'Red Hat Display SemiBold',
      fontSize: 12,
      marginTop: '8px',
      padding: '8px 12px',
    }}>
    {children}
  </Typography>
);

const RetroModal = (props: UseRetroPay) => {
  const {
    cancelReject,
    changeTarget,
    disassociableCount,
    openDisassociate,
    openReject,
    retroCandidates,
    retroCleanHours,
    retroCleanMatched,
    retroMatched,
    retroPay,
    retroPayClose,
    retroPayDisassociate,
    retroPayReject,
    retroPaySave,
    retroPayValidate,
    retroSelectedTarget,
    retroStraddles,
    retroUnmatchedCount,
    straddlingCount,
    retroWeekHours,
    retroWeekStatus,
    setReason,
  } = props;

  const isDisassociate = retroPay.reasonMode === 'disassociate';

  return (
    <Modal
      open={retroPay.visible}
      onClose={(event: any, reason: string) => {
        if (reason !== 'backdropClick') {
          retroPayClose();
        }
      }}
      disableEscapeKeyDown
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <Box sx={retroStyles.modalBox} className="model-widget">
        <Grid container alignItems="center">
          <Grid item xs={6}>
            <Typography id="modal-modal-title" component="h2">
              {!retroPay.rejectView &&
              !retroPay.rejectDone &&
              !retroPay.disassociateDone
                ? 'Retro Pay Migration'
                : isDisassociate
                ? 'Disassociate Timesheets'
                : 'Reject Retro Pay Migration'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography component={'div'} sx={retroStyles.headerActions}>
              {!retroPay.rejectView &&
                !retroPay.rejectDone &&
                !retroPay.disassociateDone && (
                  <Button
                    color="inherit"
                    disabled={
                      !retroPay.validated ||
                      retroPay.saving ||
                      !!retroPay.result ||
                      retroMatched.length === 0
                    }
                    onClick={retroPaySave}
                    variant="outlined"
                    sx={retroStyles.button}>
                    Save
                  </Button>
                )}
              <a
                href={'#'}
                onClick={retroPayClose}
                style={retroStyles.closeText}>
                Close
              </a>
            </Typography>
          </Grid>
        </Grid>
        {retroPay.loading ? (
          <Typography component={'div'} sx={retroStyles.centerBlock}>
            <CircularProgress sx={retroStyles.spinner} />
          </Typography>
        ) : retroPay.disassociateDone ? (
          <Typography component={'div'} sx={retroStyles.doneBlock}>
            <Typography sx={retroStyles.doneCheck}>&#10003;</Typography>
            <Typography sx={retroStyles.doneTitle}>
              {`${String(
                retroPay.disassociateResult?.disassociatedCount ?? 0,
              )} timesheet${
                retroPay.disassociateResult?.disassociatedCount === 1 ? '' : 's'
              } disassociated`}
            </Typography>
            <Typography sx={retroStyles.doneSubtitle}>
              {`They are filed under Disassociated Timesheets and can be attached to a project later if one appears.${
                straddlingCount > 0
                  ? ` ${String(straddlingCount)} week${
                      straddlingCount === 1 ? '' : 's'
                    } stayed on this assignment because ${
                      straddlingCount === 1 ? 'it starts' : 'they start'
                    } before it ended.`
                  : ''
              }`}
            </Typography>
          </Typography>
        ) : retroPay.rejectDone ? (
          <Typography component={'div'} sx={retroStyles.doneBlock}>
            <Typography sx={retroStyles.doneCheck}>&#10003;</Typography>
            <Typography sx={retroStyles.doneTitle}>Item dismissed</Typography>
            <Typography sx={retroStyles.doneSubtitle}>
              The note was recorded and the item has left the queue.
            </Typography>
          </Typography>
        ) : retroPay.rejectView ? (
          <>
            <Typography component={'div'} sx={retroStyles.inlineRow}>
              <Typography sx={retroStyles.fieldLabel}>Consultant</Typography>
              <Typography sx={retroStyles.consultantName}>
                {retroPay?.preview?.source?.consultantName}
              </Typography>
            </Typography>
            <Typography component={'div'} sx={retroStyles.section}>
              <Typography sx={retroStyles.fieldLabel}>
                Source Project
              </Typography>
              <Typography sx={retroStyles.projectName}>
                {retroPay?.preview?.source?.projectName}
              </Typography>
              <Typography sx={retroStyles.dateRange}>
                {`${moment(retroPay?.preview?.source?.startDate).format(
                  'MMM DD, YYYY',
                )} - ${moment(retroPay?.preview?.source?.endDate).format(
                  'MMM DD, YYYY',
                )}`}
              </Typography>
            </Typography>
            {isDisassociate ? (
              <Typography component={'div'} sx={retroStyles.section}>
                <Typography sx={retroStyles.fieldLabel}>
                  Timesheets To Disassociate
                </Typography>
                <Typography sx={retroStyles.projectName}>
                  {`${String(disassociableCount)} of ${String(
                    disassociableCount + straddlingCount,
                  )} orphaned week${
                    disassociableCount + straddlingCount === 1 ? '' : 's'
                  }`}
                </Typography>
                <Typography sx={retroStyles.dateRange}>
                  {straddlingCount > 0
                    ? `${String(straddlingCount)} week${
                        straddlingCount === 1 ? '' : 's'
                      } stay on this assignment because ${
                        straddlingCount === 1 ? 'it starts' : 'they start'
                      } before it ended.`
                    : 'No destination project exists for them.'}
                </Typography>
              </Typography>
            ) : (
              <Typography component={'div'} sx={retroStyles.section}>
                <Typography sx={retroStyles.fieldLabel}>
                  Destination Project
                </Typography>
                <Typography sx={retroStyles.projectName}>
                  {retroSelectedTarget
                    ? String(retroSelectedTarget.projectName ?? '')
                    : '-'}
                </Typography>
              </Typography>
            )}
            <Box sx={retroStyles.sectionAuto}>
              <Typography component={'div'} sx={{ paddingTop: '20px' }}>
                <Typography sx={retroStyles.reasonLabel}>
                  {retroPay.reasonMode === 'disassociate'
                    ? 'Reason for disassociating *'
                    : 'Reason for rejection *'}
                </Typography>
                <TextareaAutosize
                  minRows={3}
                  placeholder="Add Notes"
                  style={retroStyles.fullWidth}
                  value={retroPay.reason}
                  onChange={(e) => setReason(e.target.value)}
                />
                {retroPay.reasonError && !retroPay.reason && (
                  <Typography sx={retroStyles.reasonError}>
                    {retroPay.reasonMode === 'disassociate'
                      ? 'A reason is required before you can disassociate these timesheets.'
                      : 'A reason is required before you can reject this item.'}
                  </Typography>
                )}
              </Typography>
              <Typography component={'div'} sx={retroStyles.actionRow}>
                <Button
                  color="inherit"
                  onClick={cancelReject}
                  variant="outlined"
                  sx={retroStyles.button}>
                  Cancel
                </Button>
                <Button
                  disabled={retroPay.rejecting}
                  onClick={
                    retroPay.reasonMode === 'disassociate'
                      ? retroPayDisassociate
                      : retroPayReject
                  }
                  variant="contained"
                  sx={retroStyles.rejectButton}>
                  {retroPay.reasonMode === 'disassociate'
                    ? 'Disassociate Timesheets'
                    : 'Reject Item'}
                </Button>
              </Typography>
            </Box>
          </>
        ) : (
          <>
            <Typography component={'div'} sx={retroStyles.inlineRow}>
              <Typography sx={retroStyles.fieldLabel}>Consultant</Typography>
              <Typography sx={retroStyles.consultantName}>
                {retroPay?.preview?.source?.consultantName}
              </Typography>
            </Typography>
            <Grid container spacing={5} sx={retroStyles.section}>
              <Grid item sm={7}>
                <Typography sx={retroStyles.fieldLabel}>
                  Source Project
                </Typography>
                <Typography sx={retroStyles.projectName}>
                  {retroPay?.preview?.source?.projectName}
                </Typography>
                <Typography sx={retroStyles.dateRange}>
                  {`${moment(retroPay?.preview?.source?.startDate).format(
                    'MMM DD, YYYY',
                  )} - ${moment(retroPay?.preview?.source?.endDate).format(
                    'MMM DD, YYYY',
                  )}`}
                </Typography>
                <Typography sx={retroStyles.fieldLabelSpaced}>
                  Orphaned Timesheet Weeks
                </Typography>
                <TableContainer sx={retroStyles.weekTable}>
                  <Table stickyHeader aria-label="customized table">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell style={{ paddingLeft: '10px' }}>
                          Week
                        </StyledTableCell>
                        <StyledTableCell style={{ width: 80 }}>
                          Hours
                        </StyledTableCell>
                        {(retroPay.review || retroPay.result) && (
                          <StyledTableCell style={{ width: 110 }}>
                            Status
                          </StyledTableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {retroPay?.preview?.orphanedTimesheets?.length ? (
                        retroPay.preview?.orphanedTimesheets?.map(
                          (ts: RetroPayWeek) => (
                            <StyledTableRow key={ts.timesheetId}>
                              <StyledTableCell>
                                {`${moment(ts.periodFrom).format(
                                  'MMM DD',
                                )} - ${moment(ts.periodTo).format(
                                  'MMM DD, YYYY',
                                )}`}
                              </StyledTableCell>
                              <StyledTableCell>
                                {retroWeekHours(ts).toFixed(2)}
                              </StyledTableCell>
                              {(retroPay.review || retroPay.result) && (
                                <StyledTableCell>
                                  {retroWeekStatus(ts.timesheetId) ===
                                  'moves' ? (
                                    <Chip
                                      color="success"
                                      label="Migrates"
                                      size="small"
                                      variant="outlined"
                                    />
                                  ) : retroWeekStatus(ts.timesheetId) ===
                                    'split' ? (
                                    <Chip
                                      color="warning"
                                      label="Split"
                                      size="small"
                                      variant="outlined"
                                    />
                                  ) : retroWeekStatus(ts.timesheetId) ===
                                    'outside' ? (
                                    <Chip
                                      label="Outside"
                                      size="small"
                                      variant="outlined"
                                    />
                                  ) : (
                                    <Typography />
                                  )}
                                </StyledTableCell>
                              )}
                            </StyledTableRow>
                          ),
                        )
                      ) : (
                        <StyledTableRow>
                          <StyledTableCell
                            colSpan={3}
                            sx={retroStyles.emptyCell}>
                            No Records Found
                          </StyledTableCell>
                        </StyledTableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item sm={5}>
                <Typography sx={retroStyles.fieldLabel}>
                  Destination Project
                </Typography>
                {retroCandidates.length ? (
                  <>
                    <Typography component={'div'} sx={{ paddingTop: '6px' }}>
                      <Select
                        displayEmpty
                        disabled={!!retroPay.result}
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={retroPay.targetProjectConsultantId}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: '1.5px solid #E0E0E0 !important',
                            borderRadius: '8px',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: '1.5px solid #979598 !important',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            border: '1.5px solid #C4C4C4 !important',
                          },
                          fontFamily: 'Red Hat Display SemiBold',
                          fontSize: 13,
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              '& .MuiMenuItem-root': {
                                fontFamily: 'Red Hat Display SemiBold',
                                fontSize: 13,
                                whiteSpace: 'normal',
                              },
                            },
                          },
                        }}
                        onChange={(e: any) => changeTarget(e.target.value)}>
                        <MenuItem value="" disabled>
                          Select destination project
                        </MenuItem>
                        {retroCandidates.map(
                          (project: RetroPayTargetProject) => (
                            <MenuItem
                              key={project.projectConsultantId}
                              value={project.projectConsultantId}>
                              {`${String(project.projectName ?? '')} (${moment(
                                project.startDate,
                              ).format('MMM DD, YYYY')} - ${moment(
                                project.endDate,
                              ).format('MMM DD, YYYY')})`}
                            </MenuItem>
                          ),
                        )}
                      </Select>
                    </Typography>
                    {retroSelectedTarget && (
                      <Typography component={'div'} sx={{ paddingTop: '18px' }}>
                        <Typography
                          component={'div'}
                          sx={retroStyles.rowBetween}>
                          <Typography sx={retroStyles.projectNameFlush}>
                            {retroSelectedTarget.projectName}
                          </Typography>
                          {retroSelectedTarget.isSuggested && (
                            <Chip
                              label="Suggested"
                              size="small"
                              sx={{
                                '& .MuiChip-label': {
                                  color: '#1D9900',
                                  fontFamily: 'Red Hat Display SemiBold',
                                  fontSize: 9,
                                  letterSpacing: '0.5px',
                                  textTransform: 'uppercase',
                                },
                                backgroundColor: '#DDF3D8',
                                flexShrink: 0,
                                height: '20px',
                              }}
                            />
                          )}
                        </Typography>
                        <Typography sx={retroStyles.dateRange}>
                          {`${moment(retroSelectedTarget.startDate).format(
                            'MMM DD, YYYY',
                          )} - ${moment(retroSelectedTarget.endDate).format(
                            'MMM DD, YYYY',
                          )}`}
                        </Typography>
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography component={'div'} sx={retroStyles.noTargetBox}>
                    <Typography sx={retroStyles.mutedNote}>
                      No matching project found yet
                    </Typography>
                  </Typography>
                )}
                {retroPay?.preview?.affectedPayPeriods?.length ? (
                  <Typography component={'div'} sx={retroStyles.section}>
                    <Typography sx={retroStyles.fieldLabel}>
                      Affected Pay Periods
                    </Typography>
                    {retroPay.preview?.affectedPayPeriods?.map(
                      (pp: RetroPayAffectedPayPeriod) => (
                        <Typography
                          key={pp.payPeriodId}
                          component={'div'}
                          sx={retroStyles.payPeriodRow}>
                          <Typography sx={retroStyles.payPeriodDate}>
                            {`${moment(pp.startDate).format(
                              'MMM DD',
                            )} - ${moment(pp.endDate).format('MMM DD, YYYY')}`}
                          </Typography>
                          <Chip
                            color={pp.locked ? 'default' : 'success'}
                            label={pp.locked ? 'Locked' : 'Unlocked'}
                            size="small"
                            variant="outlined"
                            sx={{
                              '& .MuiChip-label': {
                                fontFamily: 'Red Hat Display SemiBold',
                                fontSize: 9,
                                letterSpacing: '0.5px',
                                textTransform: 'uppercase',
                              },
                              height: '20px',
                            }}
                          />
                        </Typography>
                      ),
                    )}
                  </Typography>
                ) : null}
              </Grid>
            </Grid>
            <Box sx={retroStyles.sectionAuto}>
              <Divider sx={{ marginTop: '10px' }} />
              <Typography component={'div'} sx={{ paddingTop: '12px' }}>
                <Typography sx={retroStyles.fieldLabel}>Messages</Typography>
                {!retroPay.review &&
                  !retroPay.result &&
                  !retroPay.saveError &&
                  retroCandidates.length > 1 &&
                  !retroPay?.preview?.suggestedTargetProjectConsultantId && (
                    <RetroMessage kind="info">
                      More than one eligible successor exists - pick the
                      destination that matches the consultant&apos;s actual new
                      assignment.
                    </RetroMessage>
                  )}
                {!retroPay.loading &&
                  retroPay.preview &&
                  retroCandidates.length === 0 && (
                    <RetroMessage kind="warning">
                      {`There is no destination project for these timesheets. Disassociate removes the ${String(
                        disassociableCount,
                      )} orphaned timesheet(s) from ${String(
                        retroPay?.preview?.source?.projectName ??
                          'this project',
                      )} and files them under Disassociated Timesheets, where they can be attached to a project later if one appears.${
                        straddlingCount > 0
                          ? ` ${String(
                              straddlingCount,
                            )} week(s) overlapping the assignment end stay in place - those hours were worked on this assignment.`
                          : ''
                      }`}
                    </RetroMessage>
                  )}
                {retroPay.review && retroMatched.length > 0 && (
                  <RetroMessage kind="success">
                    {retroUnmatchedCount === 0 && retroStraddles.length === 0
                      ? `Validation passed: all ${String(
                          retroCleanMatched.length,
                        )} week(s) with hours (${retroCleanHours.toFixed(
                          2,
                        )} h) fall inside the destination window. Ready to Save.`
                      : `Validation passed: ${String(
                          retroCleanMatched.length,
                        )} week(s) (${retroCleanHours.toFixed(
                          2,
                        )} h) migrate to the destination.`}
                  </RetroMessage>
                )}
                {retroPay.review && retroStraddles.length > 0 && (
                  <RetroMessage kind="warning">
                    {`Heads up: ${String(
                      retroStraddles
                        .map(
                          (week: RetroPayMatchedWeek) =>
                            `${moment(week.periodFrom).format(
                              'MMM DD',
                            )} - ${moment(week.periodTo).format('MMM DD')}`,
                        )
                        .join(', '),
                    )} fall(s) under both projects. After Save, split those hours manually on the consultant's timesheet page.`}
                  </RetroMessage>
                )}
                {retroPay.review &&
                  retroMatched.length > 0 &&
                  retroUnmatchedCount > 0 && (
                    <RetroMessage kind="info">
                      {`${String(
                        retroUnmatchedCount,
                      )} week(s) fall outside the destination window and stay behind - they will chain to a follow-up queue item.`}
                    </RetroMessage>
                  )}
                {retroPay.review && retroMatched.length === 0 && (
                  <RetroMessage kind="error">
                    {`Nothing migrates: no captured week overlaps the destination window${
                      retroSelectedTarget
                        ? ` (${moment(retroSelectedTarget.startDate).format(
                            'MMM DD, YYYY',
                          )} - ${moment(retroSelectedTarget.endDate).format(
                            'MMM DD, YYYY',
                          )})`
                        : ''
                    }. Pick a different destination or reject this item.`}
                  </RetroMessage>
                )}
                {retroPay.saveError && (
                  <RetroMessage kind="error">{retroPay.saveError}</RetroMessage>
                )}
                {retroPay.result && (
                  <>
                    <RetroMessage kind="success">
                      {`Done: ${String(
                        retroPay.result?.migratedTimesheets?.length || 0,
                      )} week(s) migrated to the destination project. Old rows retired. This item leaves the queue on close.`}
                    </RetroMessage>
                    {retroPay.result?.straddleTimesheets?.length > 0 && (
                      <RetroMessage kind="warning">
                        {`${String(
                          retroPay.result.straddleTimesheets.length,
                        )} week(s) span an assignment boundary and received a zero-hour placeholder - split the hours manually on the consultant's timesheet page.`}
                      </RetroMessage>
                    )}
                    {retroPay.result?.unmatchedCount > 0 && (
                      <RetroMessage kind="info">
                        {`${String(
                          retroPay.result.unmatchedCount,
                        )} week(s) fall outside the destination project.${
                          retroPay.result?.followUpQueueId
                            ? ' A follow-up queue item was created for them.'
                            : ''
                        }`}
                      </RetroMessage>
                    )}
                  </>
                )}
              </Typography>
              {!retroPay.result && (
                <Typography component={'div'} sx={retroStyles.actionRow}>
                  <Button
                    color="inherit"
                    onClick={openReject}
                    variant="outlined"
                    sx={retroStyles.button}>
                    Reject
                  </Button>
                  {retroCandidates.length === 0 && disassociableCount > 0 && (
                    <Button
                      color="inherit"
                      onClick={openDisassociate}
                      variant="outlined"
                      sx={retroStyles.button}>
                      Disassociate
                    </Button>
                  )}
                  <Button
                    color="inherit"
                    disabled={
                      !retroPay.targetProjectConsultantId ||
                      retroPay.validated ||
                      retroPay.validating
                    }
                    onClick={retroPayValidate}
                    variant="outlined"
                    sx={retroStyles.button}>
                    Validate
                  </Button>
                </Typography>
              )}
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default RetroModal;
